/**
 * §P.8 fase 2 · La exportación completa de los datos del club.
 *
 * ## «Es la puerta de salida, y tiene que existir para que la de entrada sea honesta»
 *
 * Es del contrato y es la frase que justifica la pantalla entera: «un club que no
 * puede llevarse sus datos está atrapado, no fidelizado; decirle a un maestro que
 * puede irse cuando quiera solo significa algo si hay un botón que lo hace».
 *
 * Así que el botón no está escondido en un menú ni detrás de un «contacta con
 * soporte». Está aquí, dice qué se lleva y funciona.
 *
 * ## JSON y no PDF, por tablas y sin aplanar
 *
 * «Lo que hace falta al irse es algo que otro sistema pueda leer, no algo bonito de
 * imprimir. Un PDF de ciento veinte fichas es un archivo que no sirve para nada.» Y
 * va por tablas porque quien lo recibe lo va a importar: «una tabla por bloque entra
 * sola; un objeto de veinte niveles hay que desarmarlo primero».
 *
 * La pantalla lo explica en vez de dejar que alguien abra el fichero esperando un
 * informe.
 *
 * ## Solo el dueño
 *
 * «Es todo lo que el club sabe de ciento veinte menores en un solo fichero.» El
 * guardián está en la ruta; aquí se dice por qué, para que un administrativo que no
 * lo vea sepa que no es un error.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, opcionesDeSesion } from '@/shared/api/client';
import { descargarFichero } from '@/shared/api/descargas';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fechaParaInput } from '@/shared/ui/formato';
import type { Club } from '@/club/ajustes/tipos';

export function Exportacion({ club }: { club: string }) {
  const [descargando, setDescargando] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [hecho, setHecho] = useState(false);

  /* Solo para el nombre del fichero y las cifras: el que se lleva sabe qué se lleva. */
  const datos = useQuery({
    queryKey: ['club', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Club;
    },
  });

  const exportar = async () => {
    setDescargando(true);
    setProblema(null);
    setHecho(false);

    /*
     * El nombre lleva el slug y la fecha: en la carpeta de descargas, seis meses
     * después, `kiap-dojang-pascual-2026-09-17.json` dice qué es y de cuándo.
     */
    const slug = datos.data?.slug ?? 'club';
    const fallo = await descargarFichero(
      `/v1/clubs/${club}/exports/full`,
      `kiap-${slug}-${fechaParaInput(new Date())}.json`,
      opcionesDeSesion
    );

    setDescargando(false);
    if (fallo) setProblema(fallo);
    else setHecho(true);
  };

  if (datos.isPending) {
    return <Cargando filas={3} etiqueta="Cargando el club" />;
  }

  const c = datos.data;

  return (
    <section>
      <h1>Llevarte tus datos</h1>

      <p className="tenue">
        Todo lo que Kiap guarda de tu club, en un fichero. Está aquí porque{' '}
        <strong>
          un club que no puede llevarse sus datos está atrapado, no fidelizado
        </strong>
        : decir que puedes irte cuando quieras solo significa algo si hay un
        botón que lo hace.
      </p>

      {c && (
        <dl className="datos">
          <div>
            <dt>Alumnos activos</dt>
            <dd>{c.counts.active_students}</dd>
          </div>
          <div>
            <dt>Sedes</dt>
            <dd>{c.counts.venues}</dd>
          </div>
          <div>
            <dt>Personal</dt>
            <dd>{c.counts.staff}</dd>
          </div>
        </dl>
      )}

      <h2 className="seccion">Qué te llevas, y en qué forma</h2>

      <ul className="lista-clara">
        <li>
          <strong>Un JSON, no un PDF.</strong> Lo que hace falta al irse es algo
          que otro sistema pueda leer. Un PDF de ciento veinte fichas es un
          archivo que no sirve para nada.
        </li>
        <li>
          <strong>Por tablas y sin aplanar</strong>, porque quien lo recibe lo
          va a importar: una tabla por bloque entra sola en cualquier sitio; un
          objeto de veinte niveles hay que desarmarlo primero.
        </li>
        <li>
          Dentro viene un recuento de filas por tabla, para poder comprobar
          meses después —sin Kiap delante— que la exportación estaba completa.
        </li>
        <li>
          La asistencia se limita a <strong>los dos últimos años</strong>. Es la
          única decisión de tamaño que hay ahí: todo lo demás va entero.
        </li>
      </ul>

      <p className="tenue">
        {/* Por qué un administrativo no ve esta pantalla. */}
        Solo lo puede descargar el dueño: es todo lo que el club sabe de sus
        menores en un solo fichero.
      </p>

      {problema && <PanelError problema={problema} />}

      {hecho && (
        <div className="aviso aviso-calma" role="status">
          <strong>Descargado.</strong> Guárdalo donde guardes lo importante:
          contiene datos personales de menores, así que no es un fichero para
          dejar en el escritorio.
        </div>
      )}

      <div className="acciones">
        <button
          type="button"
          onClick={() => void exportar()}
          disabled={descargando}
        >
          {descargando ? 'Preparando el fichero…' : 'Descargar todo'}
        </button>
      </div>
    </section>
  );
}
